import React, { useState, useCallback, useEffect, useRef } from 'react';
import { opportunitiesApi, documentsApi, api } from '../api/client';
import { requestAiDraft, requestAiReview, generateRequirements, buildOppContext, AiField, AiReview } from './aiAssist';
import PriceBenchmarkTab from './PriceBenchmarkTab';
import { markOnboardingStep } from '../lib/onboarding';

/* ============================================================================
 * FAR Part 11 — Describing Agency Needs (Intake)
 * All data is held in one `data` object and saved to opportunity.intake_data.
 * ==========================================================================*/

interface Props {
  opp: any;
  oppId: string;
  onSaved?: (intake: any) => void;
}

// ─── Styling ────────────────────────────────────────────────────────────────
const s = {
  section: { background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 20, marginBottom: 16 } as React.CSSProperties,
  secTitle: { fontSize: 15, fontWeight: 700, color: '#1f2937', margin: '0 0 4px' } as React.CSSProperties,
  secSub: { fontSize: 11, color: '#6b7280', margin: '0 0 16px' } as React.CSSProperties,
  label: { fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 } as React.CSSProperties,
  req: { color: '#dc2626' } as React.CSSProperties,
  help: { fontSize: 10, color: '#9ca3af', margin: '2px 0 0' } as React.CSSProperties,
  input: { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' } as React.CSSProperties,
  field: { marginBottom: 14 } as React.CSSProperties,
  th: { textAlign: 'left', padding: '6px 8px', fontSize: 10, fontWeight: 600, color: '#374151', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' } as React.CSSProperties,
  td: { padding: '4px 6px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' } as React.CSSProperties,
  btnAdd: { padding: '6px 12px', background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', marginTop: 8 } as React.CSSProperties,
  btnDel: { padding: '2px 8px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 4, fontSize: 11, cursor: 'pointer' } as React.CSSProperties,
};

export default function IntakeTab({ opp, oppId, onSaved }: Props) {
  const initial = (() => {
    try {
      const raw = opp?.intake_data;
      return raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : {};
    } catch { return {}; }
  })();

  const [data, setData] = useState<Record<string, any>>(initial);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const [showAdvanced, setShowAdvanced] = useState(
    !!(initial.brand_referenced === 'Yes' || initial.is_ict === 'Yes' || initial.is_service === 'Yes' || Object.keys(initial).length > 8)
  );

  const dataRef = useRef(data);
  useEffect(() => { dataRef.current = data; }, [data]);

  // Opportunity documents — used by the requirement gate to detect an attached
  // PWS/SOW/requirements document.
  const [docs, setDocs] = useState<any[]>([]);
  const loadDocs = useCallback(async () => {
    if (!oppId) return;
    try {
      const res = await api.get<any>(`/documents?opportunity_id=${encodeURIComponent(oppId)}`);
      setDocs(res.items || []);
    } catch { /* non-blocking */ }
  }, [oppId]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  const markDirty = () => { if (!dirtyRef.current) { dirtyRef.current = true; setDirty(true); } };
  const set = (key: string, value: any) => { markDirty(); setData(prev => ({ ...prev, [key]: value })); };
  const get = (key: string, def: any = '') => (data[key] !== undefined ? data[key] : def);

  // ─── AI Assist (advisory drafting) ───
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<Record<string, any> | null>(null);
  const [aiError, setAiError] = useState('');

  const AI_FIELDS: AiField[] = [
    { key: 'mission_need', label: 'Mission Need / Problem Statement', kind: 'long' },
    { key: 'intended_outcome', label: 'Intended Outcome', kind: 'long' },
    { key: 'functions_performed', label: 'Functions to Be Performed', kind: 'list' },
    { key: 'intended_users', label: 'Intended Users', kind: 'list' },
    { key: 'operating_environment', label: 'Operating Environment', kind: 'long' },
    { key: 'commercial_determination', label: 'Commercial Solution Determination', kind: 'long' },
    { key: 'mr_findings', label: 'Market Research Findings', kind: 'long' },
  ];

  const runAiAssist = async () => {
    setAiLoading(true); setAiError(''); setAiSuggestions(null);
    try {
      const ctx = buildOppContext(opp) + '\n\nAlready captured in intake:\n' +
        AI_FIELDS.map(f => `${f.label}: ${JSON.stringify(get(f.key, ''))}`).join('\n');
      const out = await requestAiDraft('FAR Part 11 Intake — Mission Need & Market Research', AI_FIELDS, ctx);
      if (Object.keys(out).length === 0) setAiError('AI did not return usable suggestions. Try again.');
      else setAiSuggestions(out);
    } catch (e: any) { setAiError(e.message || 'AI Assist failed.'); }
    setAiLoading(false);
  };

  const applySuggestion = (key: string) => {
    if (!aiSuggestions) return;
    set(key, aiSuggestions[key]);
    setAiSuggestions(prev => { if (!prev) return prev; const n = { ...prev }; delete n[key]; return n; });
  };
  const applyAllSuggestions = () => {
    if (!aiSuggestions) return;
    Object.keys(aiSuggestions).forEach(k => set(k, aiSuggestions[k]));
    setAiSuggestions(null);
  };

  // ─── AI Review (compliance / completeness) ───
  const [reviewLoading, setReviewLoading] = useState(false);
  const [review, setReview] = useState<AiReview | null>(null);
  const INTAKE_CHECKLIST = [
    'Mission need and intended outcome are documented.',
    'Functions and essential requirements are defined.',
    'Market research supports the requirements.',
    'Commercial products and services were considered.',
    'Performance requirements are measurable and testable.',
    'Mandatory, desired, and target requirements are distinguished.',
    'Requirements do not unnecessarily restrict competition.',
    'Restrictive requirements have documented justifications.',
    'Brand-name requirements received required approvals.',
    'Accessibility (Section 508) requirements were reviewed.',
    'Delivery and performance schedules are realistic.',
    'Acceptance criteria are defined.',
  ];
  const runAiReview = async () => {
    setReviewLoading(true); setReview(null); setAiError('');
    try {
      const ctx = buildOppContext(opp) + '\n\nIntake data:\n' + JSON.stringify(data).slice(0, 8000);
      const r = await requestAiReview('FAR Part 11 Intake', INTAKE_CHECKLIST, ctx);
      setReview(r);
    } catch (e: any) { setAiError(e.message || 'AI Review failed.'); }
    setReviewLoading(false);
  };

  // ─── AI: generate Requirements Register rows ───
  const [reqGenLoading, setReqGenLoading] = useState(false);
  const generateReqs = async () => {
    setReqGenLoading(true); setAiError('');
    try {
      const ctx = `Mission Need: ${get('mission_need')}\nIntended Outcome: ${get('intended_outcome')}\n`
        + `Functions: ${(get('functions_performed', []) || []).join('; ')}\n`
        + `Operating Environment: ${get('operating_environment')}\n` + buildOppContext(opp);
      const rows = await generateRequirements(ctx);
      if (rows.length === 0) { setAiError('AI did not return requirements. Add more detail to the mission need and try again.'); }
      else {
        const existing = get('requirements', []) || [];
        const startIdx = existing.length;
        const withIds = rows.map((r: any, i: number) => ({ ...r, req_id: `R-${String(startIdx + i + 1).padStart(3, '0')}` }));
        set('requirements', [...existing, ...withIds]);
        setMessage(`Added ${rows.length} AI-drafted requirement(s) to the register — review and edit as needed.`);
      }
    } catch (e: any) { setAiError(e.message || 'Requirement generation failed.'); }
    setReqGenLoading(false);
  };

  const save = useCallback(async () => {
    setSaving(true); setError(''); setMessage('');
    try {
      await opportunitiesApi.update(oppId, { intake_data: JSON.stringify(data) });
      setMessage('Intake saved.');
      markOnboardingStep('complete_intake');
      dirtyRef.current = false;
      setDirty(false);
      onSaved?.(data);
    } catch (e: any) {
      setError(e.message || 'Save failed.');
    }
    setSaving(false);
  }, [data, oppId, onSaved]);

  // Warn on browser close/refresh when there are unsaved changes.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirtyRef.current) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Warn when navigating away within the app (tab switch / unmount) with unsaved edits.
  useEffect(() => {
    return () => {
      if (dirtyRef.current) {
        const leave = window.confirm('You have unsaved Intake changes. Leave without saving?');
        if (!leave) {
          // Best-effort: re-persist so nothing is lost even if they meant to stay.
          opportunitiesApi.update(oppId, { intake_data: JSON.stringify(dataRef.current) }).catch(() => {});
        }
      }
    };
  }, [oppId]);

  // ─── Field primitives ──────────────────────────────────────────────────────
  const Text = (key: string, label: string, opts: { req?: boolean; help?: string; placeholder?: string } = {}) => (
    <div style={s.field}>
      <label style={s.label}>{label}{opts.req && <span style={s.req}> *</span>}</label>
      <input style={s.input} value={get(key)} placeholder={opts.placeholder || ''} onChange={e => set(key, e.target.value)} />
      {opts.help && <p style={s.help}>{opts.help}</p>}
    </div>
  );

  const Long = (key: string, label: string, opts: { req?: boolean; help?: string; placeholder?: string } = {}) => (
    <div style={s.field}>
      <label style={s.label}>{label}{opts.req && <span style={s.req}> *</span>}</label>
      <textarea style={{ ...s.input, minHeight: 70, resize: 'vertical' }} value={get(key)} placeholder={opts.placeholder || ''} onChange={e => set(key, e.target.value)} />
      {opts.help && <p style={s.help}>{opts.help}</p>}
    </div>
  );

  const Date_ = (key: string, label: string, opts: { req?: boolean } = {}) => (
    <div style={s.field}>
      <label style={s.label}>{label}{opts.req && <span style={s.req}> *</span>}</label>
      <input type="date" style={{ ...s.input, maxWidth: 220 }} value={get(key)} onChange={e => set(key, e.target.value)} />
    </div>
  );

  const Select = (key: string, label: string, options: string[], opts: { req?: boolean; help?: string } = {}) => (
    <div style={s.field}>
      <label style={s.label}>{label}{opts.req && <span style={s.req}> *</span>}</label>
      <select style={{ ...s.input, maxWidth: 420 }} value={get(key)} onChange={e => set(key, e.target.value)}>
        <option value="">Select...</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      {opts.help && <p style={s.help}>{opts.help}</p>}
    </div>
  );

  const YesNo = (key: string, label: string, options: string[] = ['Yes', 'No'], opts: { req?: boolean; help?: string } = {}) => (
    <div style={s.field}>
      <label style={s.label}>{label}{opts.req && <span style={s.req}> *</span>}</label>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {options.map(o => (
          <button key={o} type="button" onClick={() => set(key, o)} style={{
            padding: '5px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
            border: `1px solid ${get(key) === o ? '#7c3aed' : '#d1d5db'}`,
            background: get(key) === o ? '#f5f3ff' : 'white', color: get(key) === o ? '#5b21b6' : '#374151',
          }}>{o}</button>
        ))}
      </div>
      {opts.help && <p style={s.help}>{opts.help}</p>}
    </div>
  );

  // Repeatable simple text list
  const ListField = (key: string, label: string, opts: { req?: boolean } = {}) => {
    const items: string[] = get(key, []);
    return (
      <div style={s.field}>
        <label style={s.label}>{label}{opts.req && <span style={s.req}> *</span>}</label>
        {items.map((it, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
            <input style={s.input} value={it} onChange={e => { const n = [...items]; n[i] = e.target.value; set(key, n); }} />
            <button type="button" style={s.btnDel} onClick={() => set(key, items.filter((_, x) => x !== i))}>×</button>
          </div>
        ))}
        <button type="button" style={s.btnAdd} onClick={() => set(key, [...items, ''])}>+ Add</button>
      </div>
    );
  };

  const MultiSelect = (key: string, label: string, options: string[]) => {
    const sel: string[] = get(key, []);
    const toggle = (o: string) => set(key, sel.includes(o) ? sel.filter(x => x !== o) : [...sel, o]);
    return (
      <div style={s.field}>
        <label style={s.label}>{label}</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {options.map(o => (
            <button key={o} type="button" onClick={() => toggle(o)} style={{
              padding: '4px 10px', borderRadius: 9999, fontSize: 11, cursor: 'pointer',
              border: `1px solid ${sel.includes(o) ? '#10b981' : '#d1d5db'}`,
              background: sel.includes(o) ? '#d1fae5' : 'white', color: sel.includes(o) ? '#065f46' : '#374151',
            }}>{o}</button>
          ))}
        </div>
      </div>
    );
  };

  const Checklist = (key: string, items: string[]) => {
    const checked: string[] = get(key, []);
    const toggle = (o: string) => set(key, checked.includes(o) ? checked.filter(x => x !== o) : [...checked, o]);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map(o => (
          <label key={o} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#374151', cursor: 'pointer' }}>
            <input type="checkbox" checked={checked.includes(o)} onChange={() => toggle(o)} style={{ marginTop: 2 }} />
            <span>{o}</span>
          </label>
        ))}
      </div>
    );
  };

  // Generic repeatable table. columns: [{key,label,type,options?}]
  const TableField = (key: string, label: string, columns: any[], opts: { autoId?: boolean } = {}) => {
    const rows: any[] = get(key, []);
    const update = (i: number, ck: string, val: any) => { const n = rows.map((r, x) => x === i ? { ...r, [ck]: val } : r); set(key, n); };
    const addRow = () => {
      const blank: any = {};
      columns.forEach(c => { blank[c.key] = ''; });
      if (opts.autoId) blank.req_id = `R-${String(rows.length + 1).padStart(3, '0')}`;
      set(key, [...rows, blank]);
    };
    return (
      <div style={s.field}>
        <label style={s.label}>{label}</label>
        <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 6 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: columns.length * 120 }}>
            <thead><tr>
              {opts.autoId && <th style={s.th}>ID</th>}
              {columns.map(c => <th key={c.key} style={s.th}>{c.label}</th>)}
              <th style={s.th}></th>
            </tr></thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  {opts.autoId && <td style={{ ...s.td, fontFamily: 'monospace', fontSize: 10, color: '#6b7280' }}>{row.req_id || `R-${String(i + 1).padStart(3, '0')}`}</td>}
                  {columns.map(c => (
                    <td key={c.key} style={s.td}>
                      {c.type === 'select' ? (
                        <select style={{ ...s.input, fontSize: 11, padding: '4px 6px', minWidth: 110 }} value={row[c.key] || ''} onChange={e => update(i, c.key, e.target.value)}>
                          <option value="">—</option>
                          {c.options.map((o: string) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : c.type === 'long' ? (
                        <textarea style={{ ...s.input, fontSize: 11, padding: '4px 6px', minHeight: 34, minWidth: 140 }} value={row[c.key] || ''} onChange={e => update(i, c.key, e.target.value)} />
                      ) : (
                        <input style={{ ...s.input, fontSize: 11, padding: '4px 6px', minWidth: 110 }} value={row[c.key] || ''} onChange={e => update(i, c.key, e.target.value)} />
                      )}
                    </td>
                  ))}
                  <td style={s.td}><button type="button" style={s.btnDel} onClick={() => set(key, rows.filter((_, x) => x !== i))}>×</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button type="button" style={s.btnAdd} onClick={addRow}>+ Add row</button>
      </div>
    );
  };

  // File upload — reuses opportunity documents (tagged intake).
  const FileField = (label: string, category?: string) => (
    <div style={s.field}>
      <label style={s.label}>{label}</label>
      <input type="file" style={{ fontSize: 12 }} onChange={async (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        try { await documentsApi.upload(oppId, f, category); setMessage(`Uploaded ${f.name} to Documents.`); loadDocs(); }
        catch (err: any) { setError(err.message || 'Upload failed'); }
      }} />
      <p style={s.help}>Uploads to this opportunity's Documents panel.</p>
    </div>
  );

  const reqCategories = ['Functional', 'Performance', 'Technical', 'Security', 'Accessibility', 'Data', 'Integration', 'Delivery', 'Sustainability', 'Regulatory', 'Other'];

  // ── Requirement gate: PWS/SOW required OR informal requirement description ──
  // A PWS/SOW is "attached" if a document tagged sow_pws (or a requirements-doc
  // filename) exists. An informal description exists if the mission need /
  // intended outcome is written, or the Requirements Register has any row, or a
  // requirements document type + title is filled in.
  const hasPwsSowDoc = docs.some(d => {
    const cat = String(d.category || d.document_type || '').toLowerCase();
    const name = String(d.filename || d.original_filename || '').toLowerCase();
    return cat === 'sow_pws' || /\b(pws|sow|soo|statement of work|performance work)\b/.test(name);
  });
  const reqRows = (get('requirements', []) || []).filter((r: any) => r && (r.title || r.description));
  const hasInformalDescription = !!(
    String(get('mission_need', '')).trim() ||
    String(get('intended_outcome', '')).trim() ||
    reqRows.length > 0 ||
    (String(get('req_doc_type', '')).trim() && String(get('req_doc_title', '')).trim())
  );
  const requirementDefined = hasPwsSowDoc || hasInformalDescription;

  return (
    <div style={{ maxWidth: 1000 }}>
      {/* Save bar */}
      <div style={{ position: 'sticky', top: 0, zIndex: 5, background: '#f4f6fa', padding: '8px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <h2 style={{ fontSize: 18, margin: 0 }}>Intake — FAR Part 11: Describing Agency Needs</h2>
          <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>Define the need → develop requirements → address special conditions → complete reviews and approval.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {dirty && <span style={{ fontSize: 12, color: '#b45309', fontWeight: 600 }}>● Unsaved changes</span>}
          <button onClick={runAiAssist} disabled={aiLoading} style={{ padding: '10px 18px', background: aiLoading ? '#c4b5fd' : '#7c3aed', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: aiLoading ? 'default' : 'pointer' }}>
            {aiLoading ? 'Drafting...' : '✨ AI Assist'}
          </button>
          <button onClick={runAiReview} disabled={reviewLoading} style={{ padding: '10px 18px', background: reviewLoading ? '#bfdbfe' : '#2563eb', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: reviewLoading ? 'default' : 'pointer' }}>
            {reviewLoading ? 'Reviewing...' : '🔎 AI Review'}
          </button>
          <button onClick={save} disabled={saving} style={{ padding: '10px 20px', background: saving ? '#86efac' : dirty ? '#7c3aed' : '#10b981', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: saving ? 'default' : 'pointer' }}>
            {saving ? 'Saving...' : dirty ? 'Save Intake *' : 'Save Intake'}
          </button>
        </div>
      </div>
      {error && <div role="alert" style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13, marginBottom: 12 }}>{error}</div>}
      {message && <div role="status" style={{ padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, color: '#166534', fontSize: 13, marginBottom: 12 }}>{message}</div>}
      {aiError && <div role="alert" style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13, marginBottom: 12 }}>{aiError}</div>}

      {/* AI suggestions review panel (advisory — user applies) */}
      {aiSuggestions && Object.keys(aiSuggestions).length > 0 && (
        <div style={{ background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#5b21b6' }}>✨ AI-drafted suggestions (advisory — review before applying)</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={applyAllSuggestions} style={{ padding: '6px 12px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Apply all</button>
              <button onClick={() => setAiSuggestions(null)} style={{ padding: '6px 12px', background: 'white', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Dismiss</button>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {AI_FIELDS.filter(f => aiSuggestions[f.key]).map(f => (
              <div key={f.key} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 6, padding: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>{f.label}</span>
                  <button onClick={() => applySuggestion(f.key)} style={{ padding: '3px 10px', background: '#ede9fe', color: '#5b21b6', border: '1px solid #c4b5fd', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Apply</button>
                </div>
                <div style={{ fontSize: 12, color: '#4b5563', whiteSpace: 'pre-wrap' }}>{Array.isArray(aiSuggestions[f.key]) ? aiSuggestions[f.key].join(', ') : String(aiSuggestions[f.key])}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI compliance/completeness review findings (advisory) */}
      {review && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1e40af' }}>🔎 AI Compliance Review (advisory)</div>
            <button onClick={() => setReview(null)} style={{ padding: '4px 10px', background: 'white', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Dismiss</button>
          </div>
          {review.summary && <p style={{ fontSize: 12, color: '#1e3a8a', margin: '0 0 12px', fontStyle: 'italic' }}>{review.summary}</p>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', marginBottom: 6 }}>GAPS ({review.gaps.length})</div>
              {review.gaps.length === 0 ? <div style={{ fontSize: 11, color: '#9ca3af' }}>None flagged</div> :
                <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: '#78350f' }}>{review.gaps.map((g, i) => <li key={i} style={{ marginBottom: 3 }}>{g}</li>)}</ul>}
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#991b1b', marginBottom: 6 }}>COMPETITION / COMPLIANCE RISKS ({review.risks.length})</div>
              {review.risks.length === 0 ? <div style={{ fontSize: 11, color: '#9ca3af' }}>None flagged</div> :
                <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: '#b91c1c' }}>{review.risks.map((r, i) => <li key={i} style={{ marginBottom: 3 }}>{r}</li>)}</ul>}
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#5b21b6', marginBottom: 6 }}>INCONSISTENCIES ({review.inconsistencies.length})</div>
              {review.inconsistencies.length === 0 ? <div style={{ fontSize: 11, color: '#9ca3af' }}>None flagged</div> :
                <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: '#6d28d9' }}>{review.inconsistencies.map((c, i) => <li key={i} style={{ marginBottom: 3 }}>{c}</li>)}</ul>}
            </div>
          </div>
        </div>
      )}

      {/* Section 1 — Mission Need */}
      <div style={s.section}>
        <h3 style={s.secTitle}>Section 1 · Mission Need</h3>
        {Long('mission_need', 'Mission Need / Problem Statement', { req: true })}
        {Long('intended_outcome', 'Intended Outcome', { req: true })}
        {ListField('functions_performed', 'Functions to Be Performed', { req: true })}
        {ListField('intended_users', 'Intended Users')}
        {Long('operating_environment', 'Operating Environment')}
        {FileField('Supporting Intake Documents')}
      </div>

      {/* Section 2 — Market Research Support */}
      <div style={s.section}>
        <h3 style={s.secTitle}>Section 2 · Market Research Support</h3>
        {YesNo('mr_completed', 'Has FAR Part 10 market research been completed?', ['Yes', 'No', 'In Progress'], { req: true })}
        {get('mr_completed') === 'Yes' && Date_('mr_completion_date', 'Market Research Completion Date')}
        {get('mr_completed') === 'Yes' && Long('mr_findings', 'Market Research Findings')}
        {YesNo('commercial_available', 'Commercial Products or Services Available?', ['Yes', 'No', 'Partially', 'Unknown'])}
        {Long('commercial_determination', 'Commercial Solution Determination', { help: 'Explain whether commercial solutions can meet the need and what modifications may be required.' })}
        {YesNo('industry_feedback', 'Was Industry Feedback Obtained?', ['Yes', 'No', 'Not Practicable'])}
        {get('industry_feedback') === 'Yes' && Long('industry_feedback_summary', 'Industry Feedback Summary')}
        {get('industry_feedback') === 'Not Practicable' && Long('industry_feedback_reason', 'Reason Industry Feedback Was Not Practicable')}
        {FileField('Market Research Evidence')}
      </div>

      {/* Section 2b — Price Benchmark (market research: what agencies paid) */}
      <div style={s.section}>
        <h3 style={s.secTitle}>Section 2b · Price Benchmark</h3>
        <p style={s.secSub}>Historical prime-award obligations for this opportunity's NAICS/PSC — use to inform your independent government cost estimate and market research findings.</p>
        <PriceBenchmarkTab opp={opp} oppId={oppId} />
      </div>

      {/* Section 3 — Requirements Approach */}
      <div style={s.section}>
        <h3 style={s.secTitle}>Section 3 · Requirements Approach</h3>
        <p style={s.secSub}>Define the requirement one of two ways: attach a PWS / SOW / SOO (or requirements document), OR provide an informal requirement description (Mission Need / Intended Outcome above, or the Requirements Register below).</p>

        {/* Requirement gate — soft flag, consistent with the advisory posture. */}
        {requirementDefined ? (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#166534', marginBottom: 14 }}>
            ✓ Requirement defined via {hasPwsSowDoc ? 'an attached PWS/SOW document' : 'an informal requirement description'}.
          </div>
        ) : (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#92400e', marginBottom: 14 }}>
            ⚠ Requirement not yet defined. Attach a PWS/SOW (or requirements document) below, or write an informal requirement description (Mission Need / Intended Outcome, or add a Requirements Register row).
          </div>
        )}

        {Select('req_doc_type', 'Requirements Document Type', ['Document mandated by law', 'Statement of Objectives', 'Performance Work Statement', 'Statement of Work', 'Detailed design specification', 'Government standard or specification', 'Other'], { req: true })}
        {Text('req_doc_title', 'Document Title')}
        {Text('req_doc_version', 'Document Version')}
        {YesNo('performance_based', 'Performance-Based Approach Used?', ['Yes', 'No', 'Not Practicable'])}
        {get('performance_based') === 'No' && Long('performance_based_reason', 'Reason Performance-Based Approach Is Not Practicable')}
        {FileField('Requirements Document (PWS / SOW / SOO)', 'sow_pws')}
      </div>

      {/* Section 4 — Requirements Register */}
      <div style={s.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={s.secTitle}>Section 4 · Requirements Register</h3>
          <button onClick={generateReqs} disabled={reqGenLoading} style={{ padding: '6px 12px', background: reqGenLoading ? '#c4b5fd' : '#7c3aed', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: reqGenLoading ? 'default' : 'pointer' }}>
            {reqGenLoading ? 'Generating...' : '✨ AI: Generate requirements'}
          </button>
        </div>
        <p style={s.secSub}>Enter each requirement as a separate record, or use AI to draft them from the mission need (review and edit).</p>
        {TableField('requirements', 'Requirements', [
          { key: 'title', label: 'Title', type: 'text' },
          { key: 'description', label: 'Description', type: 'long' },
          { key: 'category', label: 'Category', type: 'select', options: reqCategories },
          { key: 'level', label: 'Level', type: 'select', options: ['Mandatory', 'Desired', 'Target'] },
          { key: 'type', label: 'Type', type: 'select', options: ['Functional', 'Performance', 'Physical', 'Technical', 'Regulatory'] },
          { key: 'metric', label: 'Performance Metric', type: 'text' },
          { key: 'min_level', label: 'Min Acceptable', type: 'text' },
          { key: 'target_level', label: 'Target Level', type: 'text' },
          { key: 'measurement', label: 'Measurement Method', type: 'text' },
          { key: 'acceptance', label: 'Acceptance Criteria', type: 'long' },
          { key: 'verification', label: 'Verification', type: 'select', options: ['Inspection', 'Test', 'Demonstration', 'Analysis', 'Document Review'] },
          { key: 'mr_reference', label: 'MR Reference', type: 'text' },
          { key: 'restrictive', label: 'Restrictive?', type: 'select', options: ['Yes', 'No'] },
          { key: 'rationale', label: 'Rationale', type: 'long' },
          { key: 'status', label: 'Status', type: 'select', options: ['Draft', 'Reviewed', 'Approved'] },
        ], { autoId: true })}
      </div>

      {/* Toggle for specialized sections */}
      <div style={{ ...s.section, background: '#f5f3ff', border: '1px solid #c4b5fd' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <input type="checkbox" checked={showAdvanced} onChange={e => setShowAdvanced(e.target.checked)} />
          <span style={{ fontSize: 13, fontWeight: 600, color: '#5b21b6' }}>Show specialized & review sections (5–14)</span>
        </label>
        <p style={{ ...s.help, marginLeft: 26 }}>Competition, brand-name, standards, service/AI, accessibility, sustainability, schedule, special conditions, final review, and approval.</p>
      </div>

      {showAdvanced && (
        <>
          {/* Section 5 — Competition and Restrictions */}
          <div style={s.section}>
            <h3 style={s.secTitle}>Section 5 · Competition and Restrictions</h3>
            {YesNo('promotes_competition', 'Do the requirements promote full and open competition?', ['Yes', 'No', 'Review Required'], { req: true })}
            {YesNo('restricts_competition', 'Does any requirement restrict competition?', ['Yes', 'No'], { req: true })}
            {get('restricts_competition') === 'Yes' && ListField('restricted_requirements', 'Restricted Requirements (reference by ID/title)')}
            {get('restricts_competition') === 'Yes' && Long('restriction_justification', 'Restriction Justification', { req: true })}
            {get('restricts_competition') === 'Yes' && Long('restriction_mr', 'Market Research Supporting the Restriction')}
            {YesNo('restriction_approval', 'Required Approval Obtained?', ['Yes', 'No', 'Pending'])}
            {Text('restriction_approver', 'Approver')}
            {Date_('restriction_approval_date', 'Approval Date')}
            {FileField('Supporting Documentation')}
          </div>

          {/* Section 6 — Brand-Name Requirements */}
          <div style={s.section}>
            <h3 style={s.secTitle}>Section 6 · Brand-Name Requirements</h3>
            {YesNo('brand_referenced', 'Is a Brand Name Referenced?', ['Yes', 'No'], { req: true })}
            {get('brand_referenced') === 'Yes' && <>
              {Text('brand_name', 'Brand or Product Name')}
              {Select('brand_method', 'Requirement Method', ['Brand name or equal', 'Brand-name-only', 'Manufacturer-specific feature'])}
              {ListField('salient_characteristics', 'Salient Characteristics')}
              {Long('brand_essential', 'Why Is the Brand or Feature Essential?')}
              {YesNo('brand_equivalent', 'Can an Equivalent Product Be Accepted?', ['Yes', 'No'])}
              {Long('brand_mr', 'Market Research Supporting the Decision')}
              {Select('brand_approval_status', 'Justification and Approval Status', ['Draft', 'Pending', 'Approved', 'Rejected'])}
              {Text('brand_approved_by', 'Approved By')}
              {Date_('brand_approval_date', 'Approval Date')}
              {YesNo('brand_far_clause', 'Include FAR 52.211-6?', ['Yes', 'No', 'Not Applicable'])}
            </>}
          </div>

          {/* Section 7 — Standards and Referenced Documents */}
          <div style={s.section}>
            <h3 style={s.secTitle}>Section 7 · Standards and Referenced Documents</h3>
            {YesNo('standards_referenced', 'Are External Standards or Specifications Referenced?', ['Yes', 'No'])}
            {get('standards_referenced') === 'Yes' && <>
              {TableField('standards', 'Referenced Standards', [
                { key: 'title', label: 'Standard/Doc Title', type: 'text' },
                { key: 'org', label: 'Issuing Org', type: 'text' },
                { key: 'version', label: 'Version', type: 'text' },
                { key: 'pub_date', label: 'Pub Date', type: 'text' },
                { key: 'sections', label: 'Applicable Sections', type: 'text' },
                { key: 'req_supported', label: 'Requirement Supported', type: 'text' },
                { key: 'source', label: 'Source/URL', type: 'text' },
                { key: 'attached', label: 'Attached?', type: 'select', options: ['Yes', 'No'] },
                { key: 'gov_unique', label: 'Gov-Unique?', type: 'select', options: ['Yes', 'No'] },
                { key: 'current', label: 'Active/Current?', type: 'select', options: ['Yes', 'No'] },
              ])}
              {YesNo('voluntary_consensus', 'Were voluntary consensus standards used where available?', ['Yes', 'No', 'Not Applicable'])}
              {Long('gov_unique_justification', 'Government-Unique Standard Justification')}
              {YesNo('docs_available', 'Are All Referenced Documents Available to Offerors?', ['Yes', 'No'])}
              {Select('doc_availability_method', 'Document Availability Method', ['Attached', 'URL', 'Available for Examination', 'Other'])}
            </>}
          </div>

          {/* Section 8 — Service and Government-Control Requirements */}
          <div style={s.section}>
            <h3 style={s.secTitle}>Section 8 · Service and Government-Control Requirements</h3>
            {YesNo('is_service', 'Is This a Service Requirement?', ['Yes', 'No'])}
            {get('is_service') === 'Yes' && <>
              {YesNo('inherently_governmental', 'Could the Contractor Perform an Inherently Governmental Function?', ['Yes', 'No', 'Review Required'])}
              {ListField('gov_reserved_decisions', 'Government-Reserved Decisions')}
              {ListField('gov_review_points', 'Government Review and Approval Points')}
              {YesNo('identify_as_contractors', 'Must Contractor Personnel Identify Themselves as Contractors?', ['Yes', 'No'])}
              {YesNo('mark_contractor_docs', 'Must Contractor Documents Be Marked as Contractor-Produced?', ['Yes', 'No'])}
            </>}
            {YesNo('ai_included', 'Are AI-Enabled Capabilities Included?', ['Yes', 'No'])}
            {get('ai_included') === 'Yes' && <>
              {YesNo('ai_advisory_only', 'Are AI Outputs Advisory Only?', ['Yes', 'No'])}
              <div style={s.field}>
                <label style={s.label}>Actions Requiring Human Review</label>
                {Checklist('ai_human_review', ['Requirements approval', 'Solicitation publication', 'Vendor eligibility decision', 'Proposal scoring', 'Offeror selection or rejection', 'Award decision', 'Contract modification', 'Other'])}
              </div>
              {Long('ai_oversight', 'AI Oversight and Audit Requirements')}
            </>}
          </div>

          {/* Section 9 — Accessibility and IT Requirements */}
          <div style={s.section}>
            <h3 style={s.secTitle}>Section 9 · Accessibility and IT Requirements</h3>
            {YesNo('is_ict', 'Does the Acquisition Include ICT?', ['Yes', 'No'])}
            {get('is_ict') === 'Yes' && <>
              {YesNo('section_508', 'Section 508 Applies?', ['Yes', 'No', 'Review Required'])}
              {Long('accessibility_needs', 'User Accessibility Needs')}
              {ListField('accessibility_standards', 'Applicable Accessibility Standards')}
              {Long('accessibility_testing', 'Accessibility Testing Method')}
              {YesNo('accessibility_exception', 'Accessibility Exception or Exemption Used?', ['Yes', 'No'])}
              {get('accessibility_exception') === 'Yes' && Long('accessibility_exception_reason', 'Exception or Exemption Justification')}
              {YesNo('ipv6_applies', 'Does USGv6/IPv6 Apply?', ['Yes', 'No', 'Review Required'])}
              {get('ipv6_applies') === 'No' && YesNo('cio_waiver', 'CIO Waiver Obtained?', ['Yes', 'No', 'Pending'])}
              {FileField('Accessibility or CIO Approval Evidence')}
            </>}
          </div>

          {/* Section 10 — Sustainability and Measurements */}
          <div style={s.section}>
            <h3 style={s.secTitle}>Section 10 · Sustainability and Measurements</h3>
            {YesNo('sustainability_applies', 'Do Sustainable Purchasing Requirements Apply?', ['Yes', 'No', 'Review Required'])}
            {MultiSelect('sustainability_programs', 'Applicable Sustainability Programs', ['Energy Star', 'EPEAT', 'BioPreferred', 'WaterSense', 'SNAP', 'Recovered Materials', 'Non-Ozone Depleting', 'Other'])}
            {ListField('sustainability_standards', 'Required Standards, Specifications, or Ecolabels')}
            {YesNo('sustainability_in_selection', 'Sustainability Requirements Included in Source Selection?', ['Yes', 'No', 'Not Applicable'])}
            {YesNo('sustainability_exception', 'Exception or Exemption Used?', ['Yes', 'No'])}
            {get('sustainability_exception') === 'Yes' && Long('sustainability_exception_reason', 'Exception or Exemption Justification')}
            {YesNo('metric_system', 'Is the Metric System Used?', ['Yes', 'No', 'Not Applicable'])}
            {get('metric_system') === 'No' && Long('metric_reason', 'Reason Metric Measurement Is Impracticable')}
          </div>

          {/* Section 11 — Delivery and Performance Schedule */}
          <div style={s.section}>
            <h3 style={s.secTitle}>Section 11 · Delivery and Performance Schedule</h3>
            <div style={{ display: 'flex', gap: 16 }}>
              {Date_('pop_start', 'Period of Performance — Start')}
              {Date_('pop_end', 'Period of Performance — End')}
            </div>
            {TableField('option_periods', 'Base Period and Option Periods', [
              { key: 'name', label: 'Period', type: 'text' },
              { key: 'start', label: 'Start', type: 'text' },
              { key: 'end', label: 'End', type: 'text' },
            ])}
            {TableField('milestones', 'Delivery or Performance Milestones', [
              { key: 'milestone', label: 'Milestone', type: 'text' },
              { key: 'deliverable', label: 'Deliverable', type: 'text' },
              { key: 'due', label: 'Due Date/Timeframe', type: 'text' },
              { key: 'dependency', label: 'Dependency', type: 'text' },
              { key: 'acceptance', label: 'Acceptance Criteria', type: 'long' },
              { key: 'approver', label: 'Gov Approver', type: 'text' },
            ])}
            {YesNo('schedule_mr_supported', 'Is the Schedule Supported by Market Research?', ['Yes', 'No'])}
            {Long('schedule_rationale', 'Schedule Rationale')}
            {YesNo('small_business_considered', 'Were Small-Business Capabilities Considered?', ['Yes', 'No', 'Not Applicable'])}
            {YesNo('delivery_evaluated', 'Will Delivery Time Be Evaluated in Source Selection?', ['Yes', 'No', 'Not Applicable'])}
          </div>

          {/* Section 12 — Special Conditions */}
          <div style={s.section}>
            <h3 style={s.secTitle}>Section 12 · Special Conditions</h3>
            {YesNo('liquidated_damages', 'Are Liquidated Damages Proposed?', ['Yes', 'No', 'Not Applicable'])}
            {get('liquidated_damages') === 'Yes' && <>
              {Long('ld_rationale', 'Rationale')}
              {Long('ld_harm', 'Expected Government Harm')}
              {Long('ld_calc', 'Calculation Methodology')}
              {Text('ld_rate', 'Rate')}
              {Text('ld_max', 'Maximum Amount or Period')}
              {Text('ld_clause', 'Applicable Clause')}
            </>}
            {YesNo('reconditioned', 'Are Reconditioned or Remanufactured Supplies Acceptable?', ['Acceptable', 'Prohibited', 'Acceptable with conditions', 'Not Applicable'])}
            {YesNo('priority_rating', 'Does a Priority Rating Apply?', ['Yes', 'No', 'Not Applicable'])}
            {get('priority_rating') === 'Yes' && <>{Text('priority_rating_value', 'Priority Rating')}{Text('priority_rating_authority', 'Authority')}</>}
            {YesNo('quantity_variation', 'Is a Quantity Variation Allowed?', ['Yes', 'No', 'Not Applicable'])}
            {get('quantity_variation') === 'Yes' && Text('quantity_variation_pct', 'Permitted Percentage or Quantity')}
            {YesNo('preaward_testing', 'Is Pre-Award Testing Required?', ['Yes', 'No', 'Not Applicable'])}
            {get('preaward_testing') === 'Yes' && <>
              {Long('test_purpose', 'Test Purpose')}
              {Long('test_conditions', 'Test Conditions')}
              {Long('test_criteria', 'Evaluation Criteria')}
              {Text('test_party', 'Responsible Party')}
              {Text('test_passfail', 'Pass/Fail Standard')}
            </>}
            {YesNo('telework_prohibited', 'Is Contractor Telework Prohibited?', ['Yes', 'No', 'Not Applicable'])}
            {get('telework_prohibited') === 'Yes' && <>
              {Long('telework_justification', 'Business Justification')}
              {Long('telework_co_determination', 'Contracting-Officer Determination')}
              {FileField('Approval Document')}
            </>}
          </div>

          {/* Section 13 — Final FAR Part 11 Review */}
          <div style={s.section}>
            <h3 style={s.secTitle}>Section 13 · Final FAR Part 11 Review</h3>
            {Checklist('final_review', [
              'Mission need and intended outcome are documented.',
              'Functions and essential requirements are defined.',
              'Market research supports the requirements.',
              'Commercial products and services were considered.',
              'Performance requirements are measurable and testable.',
              'Mandatory, desired, and target requirements are distinguished.',
              'Requirements do not unnecessarily restrict competition.',
              'Restrictive requirements have documented justifications.',
              'Brand-name requirements received required approvals.',
              'Referenced standards are identified and available.',
              'Inherently governmental functions are excluded.',
              'Government decision authority is preserved.',
              'Accessibility requirements were reviewed.',
              'IPv6/USGv6 requirements were reviewed.',
              'Sustainability requirements were reviewed.',
              'Delivery and performance schedules are realistic.',
              'Applicable FAR provisions and clauses were identified.',
              'Acceptance criteria are defined.',
              'All required supporting documents are attached.',
              'Legal and technical reviews are complete.',
              'Contracting officer review is complete.',
            ])}
          </div>

          {/* Section 14 — Approval */}
          <div style={s.section}>
            <h3 style={s.secTitle}>Section 14 · Approval</h3>
            {Text('requirements_owner', 'Requirements Owner')}
            {Text('technical_reviewer', 'Technical Reviewer')}
            {Text('accessibility_reviewer', 'Accessibility Reviewer (when applicable)')}
            {Text('security_reviewer', 'Security Reviewer (when applicable)')}
            {Text('legal_reviewer', 'Legal Reviewer (when required)')}
            {Text('contracting_officer', 'Contracting Officer')}
            {Long('review_comments', 'Review Comments')}
            {Select('approval_status', 'Approval Status', ['Draft', 'In Review', 'Changes Required', 'Approved'])}
            {Date_('approval_date', 'Approval Date')}
            {FileField('Approved Requirements Package')}
          </div>
        </>
      )}

      {/* Bottom save */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 40 }}>
        <button onClick={save} disabled={saving} style={{ padding: '10px 24px', background: saving ? '#86efac' : '#10b981', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: saving ? 'default' : 'pointer' }}>
          {saving ? 'Saving...' : 'Save Intake'}
        </button>
      </div>
    </div>
  );
}
